// Types pour le système de publication sociale

export type EntityType = 
  | 'accompagnement' 
  | 'evenement' 
  | 'boutique' 
  | 'bien-etre-saisonnier' 
  | 'cartes-cadeaux' 
  | 'manuel';

export type DraftStatus = 'pending' | 'ready' | 'published' | 'error';
export type Environment = 'test' | 'production';
export type RenderMode = 'legacy_image' | 'screenshot_share';

export interface SocialChannels {
  instagram_post: boolean;
  instagram_story: boolean;
  instagram_reel: boolean;
  facebook_page: boolean;
  facebook_story: boolean;
  facebook_reel: boolean;
  linkedin_page: boolean;
  linkedin_reel: boolean;
  tiktok: boolean;
  newsletter: boolean;
}

export interface CopyContent {
  default: string;
  instagram: { caption: string | null; hashtags: string | null; alt_text: string | null };
  facebook: { message: string | null };
  linkedin: { text: string | null };
  story: { text: string | null; cta: { label: string | null; url: string | null } };
  reel: { caption: string | null };
  email: { subject: string | null; preheader: string | null };
}

export interface MediaItem {
  url: string;
  type: string;
  width: number;
  height: number;
}

export interface MediaContent {
  mode: 'image' | 'image_or_carousel' | 'video_story_reel';
  cover: { url: string | null; type: string | null; width: number | null; height: number | null };
  carousel: { items: MediaItem[] };
  video: { 
    url: string | null; 
    type: string | null; 
    duration_seconds: number | null; 
    poster_url: string | null;
    width: number | null;
    height: number | null;
  };
  story: { 
    image_url: string | null; 
    video_url: string | null; 
    type: string | null; 
    width: number; 
    height: number;
  };
  reel: { 
    video_url: string | null; 
    type: string | null; 
    duration_seconds: number | null;
    poster_url: string | null;
    width: number; 
    height: number;
  };
  share?: { url: string | null; size: string };
  rendered?: { feed_url: string | null; story_url: string | null };
}

export interface Schedule {
  publish_at: string | null;
  timezone: string;
}

// Branding settings pour le template visuel
export interface BrandingSettings {
  logo: { visible: boolean };
  site_name: { visible: boolean };
  visual_title: { text: string | null };
}

export interface SocialPublicationDraft {
  id: string;
  user_id: string;
  entity_type: EntityType;
  entity_id: string | null;
  slug: string | null;
  status: DraftStatus;
  environment: Environment;
  channels: SocialChannels;
  copy: CopyContent;
  media: MediaContent;
  render_mode: RenderMode;
  share_url: string | null;
  title: string;
  schedule: Schedule;
  idempotency_key: string;
  published_at: string | null;
  last_error: string | null;
  created_at: string;
  updated_at: string;
  // Branding fields
  visual_title: string | null;
  show_logo: boolean;
  show_site_name: boolean;
}

// Payload LEGACY (compatibilité Make existant)
export interface LegacyPayload {
  environment: Environment;
  type: EntityType;
  id: string;
  title: string;
  post_text: string;
  story_text: string;
  cover_image_url: string | null;
  story_image_url: string | null;
  public_url: string | null;
  channels: {
    instagram_post: boolean;
    instagram_story: boolean;
    instagram_reel: boolean;
    facebook_page: boolean;
    facebook_story: boolean;
    linkedin_page: boolean;
    newsletter: boolean;
  };
  schedule: {
    publish_at: string | null;
    timezone: string;
  };
  meta: {
    trigger: string;
    draft_id: string;
    entity_type: EntityType;
    entity_id: string | null;
    slug: string | null;
    render_mode: RenderMode;
    idempotency_key: string;
  };
}

// Payload V2 complet (extension du LEGACY)
export interface V2Payload extends LegacyPayload {
  version: '2.0';
  source: {
    app: string;
    origin: string;
    entity: EntityType;
    entity_id: string | null;
    draft_id: string;
    updated_at: string;
  };
  content: {
    kind: EntityType;
    slug: string | null;
    title: string;
    excerpt: string | null;
    url: string | null;
    price?: { amount: number | null; currency: string; display: string | null };
    duration?: { minutes: number | null; display: string | null };
    dates?: { start: string | null; end: string | null; display: string | null };
    location?: { name: string | null; address: string | null; city: string | null };
  };
  copy: CopyContent;
  media: MediaContent & { share: { url: string | null; size: string } };
  settings: {
    timezone: string;
    dry_run: boolean;
    render: {
      mode: RenderMode;
      wait_selector: string;
      viewport: { width: number; height: number };
      device_scale_factor: number;
      full_page: boolean;
    };
    publishing: {
      use_legacy_fields: boolean;
      prefer_v2_copy: boolean;
      prefer_v2_media: boolean;
    };
    preview: {
      enabled: boolean;
      feed_size: string;
      story_size: string;
      generated_at: string;
    };
  };
  branding: BrandingSettings;
}

// Entity type mapping helpers
export const ENTITY_TYPE_LABELS: Record<EntityType, string> = {
  accompagnement: 'Accompagnements',
  evenement: 'Événements',
  boutique: 'Boutique',
  'bien-etre-saisonnier': 'Bien-être saisonnier',
  'cartes-cadeaux': 'Cartes cadeaux',
  manuel: 'Post Manuel',
};

export const ENTITY_TABLE_MAP: Record<Exclude<EntityType, 'manuel'>, string> = {
  accompagnement: 'accompaniments',
  evenement: 'events',
  boutique: 'products',
  'bien-etre-saisonnier': 'seasonal_wellness',
  'cartes-cadeaux': 'gift_card_settings',
};

export const getShareUrl = (entityType: EntityType, slug: string): string | null => {
  if (entityType === 'manuel') return null;
  return `/share/${entityType}/${slug}`;
};

// Helper pour générer le branding block V2
export const generateBrandingBlock = (draft: Pick<SocialPublicationDraft, 'show_logo' | 'show_site_name' | 'visual_title'>): BrandingSettings => ({
  logo: { visible: draft.show_logo ?? true },
  site_name: { visible: draft.show_site_name ?? true },
  visual_title: { text: draft.visual_title || null },
});
